import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useAppSelector } from 'hooks'
import { paths, constants } from 'apps'
import ViewFiAdModal from 'features/components/ViewFiAdModal'
import * as adApi from 'apis/adApi'

interface DailyAdContextType {
  hasWatchedAd: boolean | null
}

const DailyAdContext = createContext<DailyAdContextType>({ hasWatchedAd: null })

export const useDailyAd = () => useContext(DailyAdContext)

/** Get today's date string in YYYY-MM-DD format (UTC+0 timezone to match Server) */
const getTodayKey = () => new Date().toISOString().slice(0, 10)

export const DailyAdProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const auth = useAppSelector(state => state.auth)
  const [hasWatchedAd, setHasWatchedAd] = useState<boolean | null>(null)
  const [showAdModal, setShowAdModal] = useState(false)
  const lastCheckedDateRef = useRef<string | null>(null)

  const checkStatus = useCallback(async () => {
    // Exclude maintenance page and mobile redirect from ad gate
    if (window.location.pathname === paths.mobile.feature()) {
      setHasWatchedAd(true)
      return
    }
    if (window.location.pathname === paths.maintenace.detail()) {
      setHasWatchedAd(true)
      return
    }

    // Only apply to logged-in users. Guests use the site normally (with limited features).
    if (!auth.isLogged) {
      setHasWatchedAd(true)
      setShowAdModal(false)
      return
    }

    try {
      const result = await adApi.getTrackView()
      lastCheckedDateRef.current = getTodayKey()
      if (!result?.daily_ad_enabled || result?.is_watched) {
        setHasWatchedAd(true)
      } else {
        setHasWatchedAd(false)
        setShowAdModal(true)
      }
    } catch (error) {
      // Fallback: don't block user if API fails
      setHasWatchedAd(true)
    }
  }, [auth.isLogged])

  // ── Check on mount & auth change ──────────────────────────────────
  useEffect(() => {
    checkStatus()
  }, [checkStatus])

  // ── Listen for 401 ad-required events from handleAsyncRequest ─────
  useEffect(() => {
    const handleAdRequired = () => {
      if (!auth.isLogged) return
      setHasWatchedAd(false)
      setShowAdModal(true)
    }
    window.addEventListener(constants.DAILY_AD_REQUIRED_EVENT, handleAdRequired)
    return () => {
      window.removeEventListener(constants.DAILY_AD_REQUIRED_EVENT, handleAdRequired)
    }
  }, [auth.isLogged])

  // ── Re-check when user returns to tab on a new day ────────────────
  useEffect(() => {
    const handleReturnToApp = () => {
      if (document.visibilityState !== 'visible') return
      if (!auth.isLogged) return

      const today = getTodayKey()
      if (lastCheckedDateRef.current && lastCheckedDateRef.current !== today) {
        lastCheckedDateRef.current = today // Update immediately to prevent double-firing from both events
        checkStatus()
      }
    }

    document.addEventListener('visibilitychange', handleReturnToApp)
    window.addEventListener('focus', handleReturnToApp)

    return () => {
      document.removeEventListener('visibilitychange', handleReturnToApp)
      window.removeEventListener('focus', handleReturnToApp)
    }
  }, [auth.isLogged, checkStatus])

  const handleRewardPending = async () => {
    try {
      await adApi.markWatched()
      // Failed to mark ad as watched
    } finally {
      setHasWatchedAd(true)
      setShowAdModal(false)
    }
  }

  return (
    <DailyAdContext.Provider value={{ hasWatchedAd }}>
      {children}

      {showAdModal && (
        <div style={{ position: 'fixed', zIndex: 999999 }}>
          <ViewFiAdModal
            type="required"
            showCloseBtn={false}
            onClose={() => { /* no-op: user must watch */ }}
            onRewardPending={handleRewardPending}
            title="Daily Check-in Ad"
          />
        </div>
      )}
    </DailyAdContext.Provider>
  )
}
